import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthProvider";
import { api, ApiError, apiErrorMessage } from "@/api/client";
import type { ChallengeInfo } from "@/api/types";
import { AuthScreen, Field, PrimaryButton, GhostButton, ErrorNote } from "@/components/auth/ui";
import {
  supabaseEnabled,
  supabaseSignIn,
  supabaseSignUp,
  supabaseGoogleUrl,
  parseOAuthFragment,
  SupabaseAuthError,
} from "@/lib/supabase";

type Mode = "login" | "register" | "mfa";

/**
 * The hosted login. Doubles as the SSO entry point: when launched with a
 * `?challenge=…` (the IdP bounced an unauthenticated /authorize here), it shows
 * which app the user is signing in to and, after auth, resolves the challenge —
 * routing to consent or straight to the redirection modal.
 *
 * When Supabase is configured (VITE_SUPABASE_URL/_ANON_KEY), it is the primary
 * credential authority: email/password and Google both authenticate at Supabase,
 * then the access token is exchanged for a Hive session. The classic local
 * account form stays available behind a footer link (break-glass + dev).
 */
export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login, loginSupabase, loginMfa, register, status } = useAuth();

  const challenge = params.get("challenge");
  const next = params.get("next") || "/";

  const supa = supabaseEnabled();
  const [useLocal, setUseLocal] = useState(false);
  const viaSupabase = supa && !useLocal;

  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // If an SSO challenge is present, show which app we're signing in to.
  const { data: challengeInfo } = useQuery<ChallengeInfo | null>({
    queryKey: ["challenge", challenge],
    queryFn: () => (challenge ? api.get<ChallengeInfo>(`/oidc/challenge/${challenge}`) : Promise.resolve(null)),
    enabled: !!challenge,
    retry: false,
  });

  // Already authenticated + no SSO challenge → leave the login page.
  useEffect(() => {
    if (status === "authenticated" && !challenge) navigate(next, { replace: true });
  }, [status, challenge, next, navigate]);

  async function afterAuth() {
    if (!challenge) {
      navigate(next, { replace: true });
      return;
    }
    // Complete the OIDC request: consent if required, else the redirect modal.
    const res = await api.post<{ handoffId?: string; consentRequired?: boolean }>(
      "/oidc/authorize/resolve",
      { challenge },
    );
    if (res.consentRequired) {
      navigate(`/sso/consent?challenge=${challenge}`, { replace: true });
    } else if (res.handoffId) {
      navigate(`/sso/redirect?handoff=${res.handoffId}`, { replace: true });
    }
  }

  // Returning from the Supabase Google redirect: tokens arrive in the fragment.
  const fragmentHandled = useRef(false);
  useEffect(() => {
    if (!supa || fragmentHandled.current) return;
    const { accessToken, error: oauthError } = parseOAuthFragment(window.location.hash);
    if (!accessToken && !oauthError) return;
    fragmentHandled.current = true;
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    if (oauthError) {
      setError(oauthError);
      return;
    }
    setBusy(true);
    loginSupabase(accessToken!)
      .then(afterAuth)
      .catch((err) =>
        setError(err instanceof ApiError ? apiErrorMessage(err) : "Sign-in failed. Please try again."),
      )
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supa]);

  function startGoogle() {
    setError(null);
    // Come back to this exact login URL so challenge/next survive the round trip.
    const redirectTo = `${window.location.origin}/login${window.location.search}`;
    window.location.assign(supabaseGoogleUrl(redirectTo));
  }

  async function submitSupabase() {
    if (mode === "register") {
      const token = await supabaseSignUp(email, password, name || undefined);
      if (!token) {
        setNotice("Check your inbox — confirm your email, then sign in here.");
        setMode("login");
        return;
      }
      await loginSupabase(token);
    } else {
      const token = await supabaseSignIn(email, password);
      await loginSupabase(token);
    }
    await afterAuth();
  }

  async function submitLocal() {
    if (mode === "login") {
      const r = await login(email, password);
      if (r.mfaRequired) {
        setMode("mfa");
        return;
      }
    } else if (mode === "mfa") {
      await loginMfa(code);
    } else {
      await register({ email, password, name: name || undefined, orgName: orgName || undefined });
    }
    await afterAuth();
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      if (viaSupabase && mode !== "mfa") {
        await submitSupabase();
      } else {
        await submitLocal();
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? apiErrorMessage(err)
          : err instanceof SupabaseAuthError
            ? err.message
            : "Something went wrong. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  const appName = challengeInfo?.client.name;
  const title =
    mode === "mfa"
      ? "Two-factor authentication"
      : mode === "register"
        ? "Create your Hive account"
        : appName
          ? `Sign in to continue`
          : "Sign in to Hive";
  const subtitle =
    mode === "mfa"
      ? "Enter the 6-digit code from your authenticator app"
      : appName
        ? `${appName} is requesting access to your Hive identity`
        : "The operating system for AI-powered software development";

  return (
    <AuthScreen
      title={title}
      subtitle={subtitle}
      footer={
        mode !== "mfa" ? (
          <div className="flex flex-col gap-1.5">
            <button
              type="button"
              className="text-app-1 underline-offset-2 hover:underline"
              onClick={() => {
                setError(null);
                setNotice(null);
                setMode(mode === "login" ? "register" : "login");
              }}
            >
              {mode === "login" ? "Need an account? Create one" : "Have an account? Sign in"}
            </button>
            {supa && (
              <button
                type="button"
                className="text-xs text-app-2 underline-offset-2 hover:underline"
                onClick={() => {
                  setError(null);
                  setNotice(null);
                  setUseLocal(!useLocal);
                }}
              >
                {useLocal ? "Back to standard sign-in" : "Use a local Hive account instead"}
              </button>
            )}
          </div>
        ) : (
          <button type="button" className="text-app-1 hover:underline" onClick={() => setMode("login")}>
            Back to sign in
          </button>
        )
      }
    >
      {error && <ErrorNote>{error}</ErrorNote>}
      {notice && (
        <div className="mb-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-2.5 text-left text-sm text-emerald-300">
          {notice}
        </div>
      )}
      <form onSubmit={onSubmit}>
        {mode === "register" && (
          <>
            <Field label="Name" name="name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
            {!viaSupabase && (
              <Field
                label="Organization"
                name="orgName"
                placeholder="Acme Inc."
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
              />
            )}
          </>
        )}

        {mode !== "mfa" && (
          <>
            <Field
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Field
              label="Password"
              name="password"
              type="password"
              autoComplete={mode === "register" ? "new-password" : "current-password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </>
        )}

        {mode === "mfa" && (
          <Field
            label="Authentication code"
            name="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456 or recovery code"
            required
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        )}

        <PrimaryButton type="submit" loading={busy} className="mt-2">
          {mode === "register" ? "Create account" : mode === "mfa" ? "Verify" : "Continue"}
        </PrimaryButton>
      </form>

      {viaSupabase && mode !== "mfa" && (
        <>
          <div className="my-4 flex items-center gap-3 text-xs uppercase tracking-wide text-app-2">
            <span className="h-px flex-1 bg-[hsl(var(--border))]" />
            or
            <span className="h-px flex-1 bg-[hsl(var(--border))]" />
          </div>
          <GhostButton type="button" className="w-full" onClick={startGoogle} disabled={busy}>
            Continue with Google
          </GhostButton>
        </>
      )}
    </AuthScreen>
  );
}
